import React, { useCallback, useEffect, useRef, useState } from "react"

import PopupUtil, { Popup, PopupType } from "@/utils/popup"
import { isPopupDisabled } from "@/utils/harness"
import Storage from "@/services/Storage"
import AccountOwnerContactUpdateService from "@/services/AccountOwnerContactUpdate"
import { AnnouncementPopup } from "letalkui"
import { SurveySubmitProvider } from "@/contexts/SurveySubmitContext"
import { useGlobalStateStore } from "@/store/GlobalState"
import { THREE_MONTHS_IN_MILLISECONDS } from "@/utils/time"

const POPUP_PERMANENT_KEY_PREFIX = "inbox:popup:permanent-dismissed:"
const POPUP_DAY_DISMISSAL_KEY_PREFIX = "inbox:popup:day-dismissals:"
const MAX_DAY_DISMISSALS_BEFORE_PERMANENT = 7

const isPopupDismissedPermanently = (popupType: string): boolean => {
	const keyToPopupType = `${POPUP_PERMANENT_KEY_PREFIX}${popupType}`
	return Storage.get<string>(keyToPopupType) === "true"
}

const markPopupAsDismissedPermanently = (popupType: string): void => {
	const keyToPopupType = `${POPUP_PERMANENT_KEY_PREFIX}${popupType}`
	Storage.set(keyToPopupType, "true")
}

const getCalendarDate = (): string => new Date().toISOString().split("T")[0]

const popupTypeToDismissHandler: Partial<Record<PopupType, () => void>> = {
	"account-owner-contact-update": () => {
		AccountOwnerContactUpdateService.expire().catch(() => null)
	}
}

const recordPopupDayDismissal = (popupType: string): void => {
	const keyToPopupType = `${POPUP_DAY_DISMISSAL_KEY_PREFIX}${popupType}`
	const dayDismissals = Storage.get<string[]>(keyToPopupType) ?? []
	const todayDate = getCalendarDate()

	if (dayDismissals.includes(todayDate)) {
		return
	}

	const updatedDayDismissals = [...dayDismissals, todayDate]
	Storage.set(keyToPopupType, updatedDayDismissals)

	if (updatedDayDismissals.length >= MAX_DAY_DISMISSALS_BEFORE_PERMANENT) {
		markPopupAsDismissedPermanently(popupType)

		popupTypeToDismissHandler[popupType as PopupType]?.()
	}
}

const isPopupDismissedToday = (popupType: string): boolean => {
	const keyToPopupType = `${POPUP_DAY_DISMISSAL_KEY_PREFIX}${popupType}`
	const dayDismissals = Storage.get<string[]>(keyToPopupType) ?? []
	return dayDismissals.includes(getCalendarDate())
}

const Popups: React.FC = () => {
	const { instance } = useGlobalStateStore()

	const [isAccountOwnerContactUpdateEligible, setIsAccountOwnerContactUpdateEligible] = useState(false)

	useEffect(() => {
		const fetchEligibility = async () => {
			try {
				const response = await AccountOwnerContactUpdateService.checkEligibility()
				setIsAccountOwnerContactUpdateEligible(response.eligible)
			} catch {
				setIsAccountOwnerContactUpdateEligible(false)
			}
		}
		fetchEligibility()
	}, [])

	const popupTypeToMinInstanceAgeInMilliseconds: Partial<Record<PopupType, number>> = {
		"survey-value-delivery": THREE_MONTHS_IN_MILLISECONDS
	}

	const instanceAgeInMilliseconds = Date.now() - new Date(instance.instance_created_at).getTime()

	const activePopups = isPopupDisabled()
		? []
		: PopupUtil.mountPopups().filter((popup) => {
			if (isPopupDismissedPermanently(popup.type)) return false

			if (popup.type === "account-owner-contact-update" && !isAccountOwnerContactUpdateEligible) return false

			if (popup.type === "account-owner-contact-update" && isPopupDismissedToday(popup.type)) return false

			const requiredAgeInMilliseconds = popupTypeToMinInstanceAgeInMilliseconds[popup.type as PopupType]
			if (requiredAgeInMilliseconds !== undefined && instanceAgeInMilliseconds < requiredAgeInMilliseconds) return false

			return true
		})

	const [dismissedInRender, setDismissedInRender] = useState<string[]>([])

	const currentPopup: Popup | undefined = activePopups.find((popup) => !dismissedInRender.includes(popup.type))

	const submitFnRef = useRef<(() => void) | null>(null)
	const dismissFnRef = useRef<(() => void) | null>(null)
	const [isConfirmDisabled, setIsConfirmDisabled] = useState(false)
	const handleSetIsConfirmDisabled = useCallback((disabled: boolean) => setIsConfirmDisabled(disabled), [])

	if (!currentPopup) {
		return null
	}

	const dismissFromRender = () => {
		setDismissedInRender((prev) => [...prev, currentPopup.type])
	}

	const handleClose = () => {
		if (currentPopup.type === "account-owner-contact-update") {
			recordPopupDayDismissal(currentPopup.type)
		}
		dismissFromRender()
	}

	const fireDismissTracking = () => {
		dismissFnRef.current?.()
		dismissFnRef.current = null
	}

	const handleClosePermanently = () => {
		fireDismissTracking()
		markPopupAsDismissedPermanently(currentPopup.type)
		dismissFromRender()
	}

	const handleDoNotShowAgainChange = (checked: boolean) => {
		if (checked) {
			fireDismissTracking()
			markPopupAsDismissedPermanently(currentPopup.type)
		} else {
			const keyToPopupType = `${POPUP_PERMANENT_KEY_PREFIX}${currentPopup.type}`
			Storage.delete(keyToPopupType)
		}
	}

	const announcement = {
		...currentPopup,
		isConfirmDisabled: currentPopup.content ? isConfirmDisabled : undefined,
		onAction: currentPopup.content
			? () => submitFnRef.current?.()
			: currentPopup.onAction
	}

	return (
		<SurveySubmitProvider submitFnRef={submitFnRef} dismissFnRef={dismissFnRef} onClosePermanently={handleClosePermanently} setIsConfirmDisabled={handleSetIsConfirmDisabled}>
			<AnnouncementPopup
				announcement={announcement}
				open={true}
				onClose={handleClose}
				onClosePermanently={handleClosePermanently}
				onDoNotShowAgainChange={handleDoNotShowAgainChange}
				shouldHideDoNotShowAgain={currentPopup.shouldHideDoNotShowAgain}
			/>
		</SurveySubmitProvider>
	)
}

export default Popups
